/**
 * lrb-params.js — the email parameter contract
 * Leaps & Rebounds landing pages
 *
 * Liquid cannot read query parameters, so every parameter-driven decision on a
 * landing page happens here. This file is deliberately independent of
 * lrb-personalization.js: the offer copy must survive a cold Railway container,
 * a failed sections request, or the personalization layer not being on the page
 * at all. It touches no network.
 *
 * The contract, as the email marketer writes it:
 *   ?lrb_offer=storecredit        the slug sections target on
 *   ?lrb_offer_text=$35 off       the offer as the shopper reads it
 *   ?lrb_code=BOUNCE35            shown, and applied by the button
 *   ?lrb_ends=2026-09-30          starts the countdown (store time, end of day)
 *   ?lrb_angle=warranty           ranks the matching proof sections up
 *
 * There is deliberately no headline override: the hero eyebrow carries
 * lrb_offer_text, and a parameter nothing reads is a trap for whoever writes the
 * link. Add one here only together with the markup that honours it.
 *
 * The markup contract, as the snippets use it:
 *   data-lrb-var="lrb_code"                     text replaced with the value
 *   data-lrb-show="lrb_offer=storecredit,freeship"   kept only when it matches
 *   data-lrb-hide="lrb_offer"                   dropped when that param is present
 *   data-lrb-countdown                          ticks down to lrb_ends
 *   data-lrb-urgency                            the block a dead countdown removes
 *   data-lrb-code-apply                         link keeps its href and gains ?discount=<code>
 *
 * Every parameter is optional. With none of them the page must still read as a
 * coherent page, because links get forwarded and mail clients strip query
 * strings. Values are attacker-controllable, so text is written with
 * textContent and the discount code is character-restricted.
 */

(() => {
  'use strict';

  if (window.LRB?.applyOfferCopy) return; // already loaded

  const PARAM_KEYS = ['lrb_offer', 'lrb_offer_text', 'lrb_angle', 'lrb_code', 'lrb_ends'];

  // Anyone can craft a link, so a value that lands in shopper-visible copy is
  // capped. It cannot stop someone writing a wrong offer into a URL, but it does
  // stop a pasted essay from taking the layout apart.
  const MAX_TEXT = 120;

  // lrb_ends is read in the store's timezone, so an offer ends at the same
  // moment for every shopper. Keep in step with Settings → General → Time zone.
  const STORE_TZ = 'America/Los_Angeles';

  // A countdown filled into a clone that never reaches the page is dropped
  // after this long, so it does not tick forever.
  const DETACHED_GRACE_MS = 10000;

  const params = {};
  const qs = new URLSearchParams(window.location.search);
  for (const k of PARAM_KEYS) {
    const v = (qs.get(k) || '').trim();
    if (v) params[k] = v;
  }

  // Milliseconds to add to UTC to get wall-clock time in tz at instant t.
  const tzOffset = (t, tz) => {
    const parts = {};
    const format = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });
    for (const { type, value } of format.formatToParts(new Date(t))) parts[type] = +value;
    const wall = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second
    );
    return wall - Math.floor(t / 1000) * 1000;
  };

  // A wall-clock time in the store's timezone, as a Date. The second pass
  // corrects a guess that landed on the other side of a DST change.
  const storeTime = (y, mo, d, h, mi, s) => {
    const wall = Date.UTC(y, mo - 1, d, h, mi, s);
    try {
      const t = wall - tzOffset(wall, STORE_TZ);
      return new Date(wall - tzOffset(t, STORE_TZ));
    } catch {
      // No timezone support: the visitor's local time is the closest we have.
      return new Date(y, mo - 1, d, h, mi, s);
    }
  };

  const parseDeadline = raw => {
    if (!raw) return null;
    // YYYY-MM-DD, or YYYY-MM-DDTHH:MM[:SS] with no offset, is store time. A date
    // alone means the end of that day: an offer that "ends September 30" runs
    // to 23:59:59 on the 30th.
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(raw.trim());
    if (m) {
      const [y, mo, d] = [+m[1], +m[2], +m[3]];
      // Reject dates that do not exist (2026-02-31) rather than letting them
      // roll over into the next month.
      const check = new Date(Date.UTC(y, mo - 1, d));
      if (check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d) return null;
      if (m[4] === undefined) return storeTime(y, mo, d, 23, 59, 59);
      const [h, mi, sec] = [+m[4], +m[5], +(m[6] || 0)];
      if (h > 23 || mi > 59 || sec > 59) return null;
      return storeTime(y, mo, d, h, mi, sec);
    }
    // Anything else (an explicit offset or Z) is taken as written.
    const dt = new Date(raw);
    return isNaN(dt.getTime()) ? null : dt;
  };

  // An expired link reads as a link with no offer, so the offer band stays
  // hidden instead of advertising a code checkout will reject.
  // lrb_angle is targeting, not offer, so it survives.
  let deadline = parseDeadline(params.lrb_ends);
  if (deadline && deadline.getTime() <= Date.now()) {
    for (const k of ['lrb_offer', 'lrb_offer_text', 'lrb_code', 'lrb_ends']) delete params[k];
    deadline = null;
  }

  let countdowns = [];
  let timer = null;

  const paramMatches = expr => {
    // "lrb_offer=a,b" is true when lrb_offer is a or b. "lrb_offer" is true when present.
    if (!expr) return false;
    const eq = expr.indexOf('=');
    if (eq === -1) return !!params[expr.trim()];
    const key = expr.slice(0, eq).trim();
    const want = expr
      .slice(eq + 1)
      .split(',')
      .map(v => v.trim().toLowerCase());
    const have = (params[key] || '').toLowerCase();
    return !!have && want.includes(have);
  };

  const formatRemaining = ms => {
    const m = Math.floor(ms / 60000) % 60;
    const h = Math.floor(ms / 3600000) % 24;
    const d = Math.floor(ms / 86400000);
    if (d > 0) return `${d}d ${h}h ${m}m`;
    const sec = Math.floor(ms / 1000) % 60;
    return `${h}h ${m}m ${sec}s`;
  };

  const tick = () => {
    const now = Date.now();
    countdowns = countdowns.filter(c => {
      // A clone is filled before it is appended, so it is legitimately detached
      // on the first tick. Drop it once it has been on the page and left, or if
      // it never arrived.
      if (c.el.isConnected) c.seen = true;
      else if (c.seen || now - c.added > DETACHED_GRACE_MS) return false;
      const left = c.ends - now;
      if (left <= 0) {
        // Drop the whole urgency block rather than leave a zeroed clock
        // promising a deal that has ended.
        (c.el.closest('[data-lrb-urgency]') || c.el).remove();
        return false;
      }
      c.el.textContent = formatRemaining(left);
      return true;
    });
    if (!countdowns.length && timer) {
      clearInterval(timer);
      timer = null;
    }
  };

  const applyOfferCopy = root => {
    if (!root?.querySelectorAll) return;

    // Conditionals first, so we never fill copy we are about to remove.
    for (const el of root.querySelectorAll('[data-lrb-show]')) {
      if (paramMatches(el.getAttribute('data-lrb-show'))) {
        // Liquid ships these hidden so nothing flashes before we decide.
        el.removeAttribute('hidden');
        el.classList.remove('lrb-hidden');
      } else {
        el.remove();
      }
    }
    for (const el of root.querySelectorAll('[data-lrb-hide]')) {
      if (paramMatches(el.getAttribute('data-lrb-hide'))) el.remove();
    }

    // No parameter means the Liquid fallback copy stands.
    for (const el of root.querySelectorAll('[data-lrb-var]')) {
      const val = params[el.getAttribute('data-lrb-var')];
      if (!val) continue;
      el.textContent = val.length > MAX_TEXT ? `${val.slice(0, MAX_TEXT).trim()}…` : val;
    }

    // Restricted to the characters Shopify allows in a discount URL, so a
    // hand-edited parameter cannot build a different link.
    const code = (params.lrb_code || '').replace(/[^A-Za-z0-9_-]/g, '');
    // The link keeps its own destination and gains ?discount=, which Shopify
    // turns into the discount_code cookie. /discount/<code>?redirect= would cost
    // a round trip and reset a PDP's variant (see lrb-fit-finder).
    if (code) {
      for (const el of root.querySelectorAll('[data-lrb-code-apply]')) {
        if (el.tagName !== 'A') continue;
        try {
          const url = new URL(el.getAttribute('href') || '/', window.location.href);
          url.searchParams.set('discount', code);
          el.href =
            url.origin === window.location.origin ? url.pathname + url.search + url.hash : url.href;
        } catch {
          /* unparseable href: leave the link alone */
        }
      }
    }

    // Only an lrb_ends that parses starts a clock; anything else leaves the
    // static fallback text alone.
    if (deadline) {
      const ends = deadline.getTime();
      const els = root.querySelectorAll('[data-lrb-countdown]');
      for (const el of els) {
        // Without this a screen reader announces the countdown once a second.
        el.setAttribute('role', 'timer');
        el.setAttribute('aria-live', 'off');
        countdowns.push({ el, ends, added: Date.now() });
      }
      if (els.length) {
        tick();
        if (countdowns.length && !timer) timer = setInterval(tick, 1000);
      }
    }
  };

  window.LRB = window.LRB || {};
  Object.assign(window.LRB, { PARAM_KEYS, params, paramMatches, applyOfferCopy });

  const runOnce = () => applyOfferCopy(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runOnce);
  } else {
    runOnce();
  }
})();
